import { useEffect } from 'react';

import { FreebetPayoutStatus, useFreebetsStore } from '@/entities/freebet';
import type { FreebetsStatus } from '@/entities/freebet';
import { useSessionStore } from '@/entities/session';
import { useClaimFreebets } from '@/features/claim-freebets';
import { DisplayNameForm } from '@/features/edit-display-name';
import { formatMoscowDateTime, formatScore } from '@/shared/lib';
import { openLink } from '@/shared/lib/telegram';
import { Button, Screen } from '@/shared/ui';

import styles from './ProfilePage.module.scss';

/**
 * Что происходит с Фрибетами игрока — словами.
 *
 * Счётчик сам по себе не объясняет, почему кнопки нет: право
 * зафиксировано, но условия (телефон и подписка) могут быть не выполнены,
 * заявка может быть в пути или уже просрочена (ГДД §4.4).
 */
function payoutHint(status: FreebetsStatus): string {
  if (status.earnedThisCycle === 0) return 'за этот цикл пока ничего';

  switch (status.payoutStatus) {
    case FreebetPayoutStatus.AWAITING_CONDITIONS:
      return status.hasPhone
        ? 'ждём подписку на канал'
        : 'нужен номер телефона';
    case FreebetPayoutStatus.READY:
    case FreebetPayoutStatus.SENDING:
      return 'передаём букмекеру';
    case FreebetPayoutStatus.CONFIRMED:
      return 'готовы к получению';
    case FreebetPayoutStatus.PHONE_NOT_LINKED:
      return 'номер не связан с аккаунтом BetBoom';
    case FreebetPayoutStatus.FAILED:
      return 'передача не удалась, право сохранено';
    case FreebetPayoutStatus.EXPIRED:
      return 'срок получения истёк';
    default:
      // Заявки ещё нет: выдача идёт после конца цикла, и ненулевой
      // счётчик без заявки — норма, а не ошибка
      return 'выдадим после конца цикла';
  }
}

export function ProfilePage() {
  const user = useSessionStore((state) => state.user);
  const freebets = useFreebetsStore((state) => state.status);
  const freebetsError = useFreebetsStore((state) => state.error);
  const loadFreebets = useFreebetsStore((state) => state.load);
  const { claim, isClaiming, error: claimError } = useClaimFreebets();

  useEffect(() => {
    void loadFreebets();
  }, [loadFreebets]);

  if (!user) return null;

  // Ссылку собирает СЕРВЕР: адрес мини-аппа живёт в его окружении.
  // Раньше здесь стояло `t.me/share/url?url=` + `referralStartParam`,
  // то есть в «поделиться» уходил голый `ref_КОД` — друг получал
  // бессмысленную строку вместо приглашения, и реферал не работал.
  const shareLink = user.referralLink
    ? `https://t.me/share/url?url=${encodeURIComponent(user.referralLink)}`
    : null;

  return (
    <Screen title="Профиль">
      <dl className={styles.facts}>
        <div className={styles.fact}>
          <dt>Подписка на канал</dt>
          <dd data-testid="subscription-status">
            {user.isSubscribed ? 'есть' : 'нет'}
          </dd>
        </div>
        <div className={styles.fact}>
          <dt>Телефон</dt>
          <dd>{user.hasPhone ? 'передан' : 'не передан'}</dd>
        </div>
        <div className={styles.fact}>
          <dt>Реферальный код</dt>
          <dd data-testid="referral-code">{user.referralCode}</dd>
        </div>
        {freebets ? (
          <div className={styles.fact}>
            <dt>Фрибеты за цикл</dt>
            <dd data-testid="freebets-status">
              {formatScore(freebets.earnedThisCycle)} — {payoutHint(freebets)}
              {freebets.claimDeadlineAt
                ? `, до ${formatMoscowDateTime(freebets.claimDeadlineAt)}`
                : ''}
            </dd>
          </div>
        ) : null}
      </dl>

      {freebetsError ? (
        <p className={styles.error} role="alert" data-testid="freebets-error">
          {freebetsError}
        </p>
      ) : null}

      {freebets?.canClaim ? (
        <Button
          className={styles.share}
          disabled={isClaiming}
          data-testid="claim-freebets"
          onClick={() => {
            void claim();
          }}
        >
          Получить Фрибеты
        </Button>
      ) : null}

      {claimError ? (
        <p className={styles.error} role="alert" data-testid="claim-error">
          {claimError}
        </p>
      ) : null}

      <DisplayNameForm />

      {shareLink ? (
        <Button
          variant="ghost"
          className={styles.share}
          data-testid="invite-friend"
          onClick={() => {
            openLink(shareLink);
          }}
        >
          Позвать друга
        </Button>
      ) : null}
    </Screen>
  );
}
